import { z } from "zod";

import { UserStatus } from "@/enum";
import { DraftReplies } from "@/enum/draft-replies.enum";
import { ConnectionScrapingInterval } from "@/enum/connection-scraping-interval.enum";
import { EOnboardingStep } from "@/enum/onboarding-step.enum";
import { EOnboardingVariant } from "@/enum/onboarding-variant.enum";
import { UserLanguage } from "@/enum/user.enum";
import { EmailSchema } from "@/pages/Private/pages/Account/schema/email";
import { PeopleSchema } from "@/pages/Private/pages/Apollo/schema/get-people";
import { SubscriptionSchema } from "@/pages/Private/pages/Subscription/schema/subscription";
import { UserRoleSchema } from "@/pages/Private/pages/UserRoles/schema/roles";
import { MetaDataSchema } from "@/types/meta-data";
import { ERROR_TYPE } from "@/utils";
import { LeadScoringBreakdownSchema } from "@/pages/Private/pages/Apollo/schema/lead-scoring-breakdown.schema";

const { REQUIRED, INVALID_EMAIL, INVALID_VALUE } = ERROR_TYPE;

export const LoginSchema = z.object({
	email: z.string().min(1, { message: REQUIRED }).email(INVALID_EMAIL),
	password: z.string().min(1, { message: REQUIRED }).trim(),
});

export const LoginRequestSchema = z.object({
	email: z.string().email(INVALID_EMAIL),
	password: z.string().min(8, { message: INVALID_VALUE }),
});

export const CompanySchema = z
	.object({
		id: z.number().gt(0),
		name: z.string().nullable(),
		linkedinUrl: z.string().nullable(),
		hasApolloApiKey: z.boolean(),
		hasLemlistApiKey: z.boolean(),
		hasHubspotApiKey: z.boolean(),
		website: z.string().nullable(),
		personas: z.array(z.string()).nullable(),
		industries: z.array(z.string()).nullable(),
		size: z.string().nullable(),
		location: z.string().nullable(),
		fromOrganization: z.object({
			urn: z.string().nullable(),
			image: z.string().nullable(),
			subtitle: z.string().nullable(),
			title: z.string().nullable(),
		}),
		fromMember: z.object({
			urn: z.string().nullable(),
			image: z.string().nullable(),
			title: z.string().nullable(),
			subtitle: z.string().nullable(),
		}),
		keywords: z.string().nullable(),
		stripeCustomerId: z.string().nullable(),
		stripeSubscriptionId: z.string().nullable(),
		isOnboarded: z.boolean(),
		subscriptions: z.array(SubscriptionSchema).nullable(),
		bought5Lists: z.boolean(),
		demoEndsAt: z.string().nullable(),
		isDemo: z.boolean().optional(),
		allowInstallments: z.boolean().optional(),
		draftReplies: z.nativeEnum(DraftReplies).nullable(),
	})
	.nullable();

export const FetchMeResponseSchema = z.object({
	id: z.number().gt(0),
	// Direct signups (pre-checkout onboarding) have no name until the LinkedIn
	// profile enrichment fills it in — accept null and coerce to "" so the rest
	// of the app keeps its plain-string contract.
	firstName: z
		.string()
		.nullable()
		.transform((value) => value ?? ""),
	lastName: z
		.string()
		.nullable()
		.transform((value) => value ?? ""),
	email: z.string().email(INVALID_EMAIL),
	linkedinUrl: z.string().optional().nullable(),
	is2FAEnabled: z.boolean(),
	isBlocked: z.boolean(),
	status: z.nativeEnum(UserStatus),
	userLanguage: z.nativeEnum(UserLanguage),
	company: CompanySchema,
	roles: z.array(UserRoleSchema),
	prospects: z.array(PeopleSchema).optional(),
	autoAccept: z.boolean().optional(),
	profileVisits: z.boolean().optional(),
	connectionScraping: z.boolean().optional(),
	connectionScrapingInterval: z.nativeEnum(ConnectionScrapingInterval).nullable().optional(),
	lastConnectionSyncAt: z.string().nullable().optional(),
	city: z.string().optional(),
	country: z.string().optional(),
	hasLiSn: z.boolean().optional(),
	hasProxy: z.boolean().optional(),
	isOnboarded: z.boolean(),
	hasLinkedinPremium: z.boolean().optional(),
	hasAdminRights: z.boolean().optional(),
	isSupport: z.boolean().optional(),
	tipsShown: z.boolean(),
	isp: z.string().optional(),
	region: z.string().optional(),
	timezone: z.string().optional(),
	ipRoyalOrderNumber: z.number().optional().nullable(),
	unipile: z
		.object({
			id: z.number(),
			isLoggedIn: z.boolean(),
			salesNavigatorIsLoggedIn: z.boolean().optional(),
			status: z.string().optional(),
		})
		.optional(),
	linkedinAllowed: z.boolean(),
	emailVerifiedAt: z.string().nullable().optional(),
	onboardingStep: z.nativeEnum(EOnboardingStep).nullable().optional(),
	// Server-derived (PREC-3719): which onboarding flow this user still has to run.
	// Absent on the few user payloads that are not "the user about themselves".
	onboardingVariant: z.nativeEnum(EOnboardingVariant).optional(),
	invitedAt: z.string().nullable().optional(),
	visitsPerDay: z.number().optional(),
	connectionRequestsPerDay: z.number().optional(),
	messagesPerDay: z.number().optional(),
	autoWithdrawInvitesEnabled: z.boolean().optional(),
	autoWithdrawInvitesDays: z.number().optional(),
	isLoggedInInLinkedin: z.boolean(),
	emails: z.array(EmailSchema).optional(),
	// PREC-3686: the sender's AI copywriting languages. Only the users overview
	// eager-loads them, so every other user payload leaves this null.
	copywritingLanguages: z
		.object({
			primaryLanguage: z.string().nullable(),
			secondaryLanguages: z.array(z.string()),
			fallbackLanguage: z.string().nullable(),
		})
		.nullable()
		.optional(),
});

const ConnectionScoreCompanyCategoryResultSchema = z
	.object({
		reason: z.string().nullable().optional(),
		leadScore: z.string().nullable().optional(),
		leadScoringReasoning: z.string().nullable().optional(),
		leadScoringBreakdown: LeadScoringBreakdownSchema.nullable().optional(),
		scoreCompanyCategory: z.object({ category: z.string() }).nullable().optional(),
	})
	.nullable()
	.optional();

const ConnectionOrganizationSchema = z
	.object({
		name: z.string().nullable().optional(),
		logoUrl: z.string().nullable().optional(),
		linkedinUrl: z.string().nullable().optional(),
		country: z.string().nullable().optional(),
		industry: z.string().nullable().optional(),
		estimatedNumEmployees: z.number().nullable().optional(),
		foundedYear: z.number().nullable().optional(),
		scoreCompanyCategoryResult: ConnectionScoreCompanyCategoryResultSchema,
	})
	.nullable()
	.optional();

const ConnectionScorePersonaCategoryResultSchema = z
	.object({
		personaScore: z.string().nullable().optional(),
		companyScore: z.string().nullable().optional(),
		reason: z.string().nullable().optional(),
		scorePersonaCategory: z.object({ category: z.string() }).nullable().optional(),
	})
	.nullable()
	.optional();

export const MyConnectionsSchema = z.object({
	id: z.number(),
	linkedinUrl: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	companyName: z.string(),
	title: z.string(),
	email: z.string().nullable().optional(),
	emailStatus: z.string().nullable().optional(),
	phone: z.string().nullable().optional(),
	phoneStatus: z.string().nullable().optional(),
	connectedAt: z.string(),
	isEnriched: z.boolean(),
	prospectId: z.number().nullable().optional(),
	country: z.string().nullable().optional(),
	language: z.string().nullable().optional(),
	lastEnrichedAt: z.string().nullable().optional(),
	overallScore: z.string().nullable().optional(),
	seniority: z.string().nullable().optional(),
	gender: z.string().nullable().optional(),
	genderProbability: z.number().nullable().optional(),
	city: z.string().nullable().optional(),
	organization: ConnectionOrganizationSchema,
	scorePersonaCategoryResult: ConnectionScorePersonaCategoryResultSchema,
});

export const MyConnectionsResponseSchema = z.object({
	data: z.array(MyConnectionsSchema),
	meta: MetaDataSchema,
});

export const LoginResponseSchema = z.object({
	accessToken: z.string().optional(),
	otpToken: z.string().optional(),
	user: FetchMeResponseSchema,
});

export const GoogleAuthSchema = z.object({
	clientId: z.string(),
	credential: z.string(),
	select_by: z.string(),
});

export type LoginType = z.infer<typeof LoginSchema>;
export type User = z.infer<typeof FetchMeResponseSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type GoogleAuth = z.infer<typeof GoogleAuthSchema>;
export type MyConnectionsResponse = z.infer<typeof MyConnectionsResponseSchema>;
export type MyConnections = z.infer<typeof MyConnectionsSchema>;
